import { X } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useReturnFocus } from '@/components/ui/focus';
import { STATE_NOTES } from './resolve';
import { bodyOf, type TourContext, type TourStep } from './steps';
import { useTourTarget } from './useTourTarget';

type CoachmarkProps = {
  step: TourStep;
  index: number;
  total: number;
  ctx: TourContext;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
  /** The step's own action (`step.action`), in place of Next. */
  onAction: () => void;
};

/** Room around a highlighted region, so its border is not hidden under the ring. */
const PAD = 6;

/** Where the card sits with nothing to point at: centred, a third of the way down. */
const centred = (): DOMRect => new DOMRect(window.innerWidth / 2, window.innerHeight / 3, 0, 0);
/** On a phone the card is docked at the bottom, under a region scrolled to the top. */
const docked = (): DOMRect => new DOMRect(window.innerWidth / 2, window.innerHeight - 16, 0, 0);

/**
 * One tour step: the region it explains, lit inside a dimmed page, and a card
 * beside it. The page under the dim does not respond to clicks (the tour only
 * explains); Esc or × ends the tour, ←/→ step through it. With no region to
 * light (still loading, or missing), the card sits centred and says why.
 */
export function Coachmark({ step, index, total, ctx, onBack, onNext, onClose, onAction }: CoachmarkProps) {
  const { mobile } = ctx;
  const target = useTourTarget(step, mobile);
  const card = useRef<HTMLDivElement>(null);
  const returnFocus = useReturnFocus();
  const { rect } = target;
  const anchor = useMemo(() => ({ current: { getBoundingClientRect: () => (mobile ? docked() : (rect ?? centred())) } }), [rect, mobile]);
  const last = index === total - 1;
  const note = target.state === 'content' ? null : STATE_NOTES[target.state];

  // Each step is announced from its title: focus moves to the card on every step.
  useEffect(() => { card.current?.focus(); }, [step.id]);

  // ←/→ wherever focus is while the tour is open: the page under it takes no input.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowRight' && !step.action) {
        event.preventDefault();
        onNext();
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        onBack();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [step.action, index, onNext, onBack]);

  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-70">
        {rect ? (
          <div
            className="absolute rounded-card shadow-[0_0_0_9999px_var(--color-scrim)] outline-2 outline-accent motion-safe:transition-[top,left,width,height] motion-safe:duration-150"
            style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
          />
        ) : (
          <div className="absolute inset-0 bg-scrim" />
        )}
      </div>
      <Popover open>
        <PopoverAnchor virtualRef={anchor} />
        <PopoverContent
          ref={card}
          tabIndex={-1}
          aria-label={`Product tour, step ${index + 1} of ${total}: ${step.title}`}
          side={mobile ? 'top' : 'bottom'}
          align={rect && !mobile ? 'start' : 'center'}
          sideOffset={rect && !mobile ? PAD + 8 : 0}
          // A region taller than the screen still leaves the card on screen.
          sticky="always"
          width="auto"
          className="z-71 max-h-none w-[min(360px,calc(100vw-32px))] p-4 focus-visible:shadow-pop"
          onOpenAutoFocus={(event) => {
            returnFocus.onOpenAutoFocus();
            event.preventDefault();
            card.current?.focus();
          }}
          onCloseAutoFocus={returnFocus.onCloseAutoFocus}
          onEscapeKeyDown={onClose}
          onInteractOutside={(event) => { event.preventDefault(); }}
        >
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-xs text-muted" aria-hidden="true">
              {index + 1} of {total}
            </span>
            <Button size="icon-sm" variant="ghost" aria-label="Close the tour" className="-mr-1.5 ml-auto" onClick={onClose}>
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>
          <h2 className="m-0 text-base font-semibold">{step.title}</h2>
          <p className="mt-1.5 mb-0 text-sm text-fg">{bodyOf(step, ctx)}</p>
          {note ? <p className="mt-2 mb-0 text-sm text-muted">{note}</p> : null}
          <div className="mt-4 flex items-center gap-2">
            {index > 0 ? (
              <Button size="sm" variant="ghost" onClick={onBack}>
                Back
              </Button>
            ) : null}
            <span className="ml-auto" />
            {step.action === 'connect' ? (
              <Button size="sm" variant="primary" onClick={onAction}>
                Connect your ad networks
              </Button>
            ) : (
              <Button size="sm" variant="primary" onClick={onNext}>
                {last ? 'Finish' : 'Next'}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
